import { memo } from "react";
import { Handle, Position } from "@xyflow/react";
import {
  Box,
  Text,
  Flex,
  IconButton,
  Tooltip,
  useColorModeValue,
  HStack,
  Popover,
  PopoverTrigger,
  PopoverContent,
  PopoverBody,
  PopoverHeader,
  PopoverCloseButton,
  Portal,
  Icon
} from "@chakra-ui/react";
import { getFilterDisplayString } from "utils/nameUtils";
import { StepInterface } from "./Interface";
import Recommendations from "./Recommendations";
import { useAnalysisPlanContext } from "context/batch/AnalysisPlanContext";
import { FaFilter, FaTrash } from "react-icons/fa6";
import { AiFillPieChart } from "react-icons/ai";
import { MdBarChart } from "react-icons/md";
import { FaLongArrowAltRight } from "react-icons/fa";
import { TbMathFunction } from "react-icons/tb";

interface ICustomNodeProps {
  data: StepInterface;
  selected?: boolean;
}

const AnalysisStepNode = ({ data: currentNode, selected }: ICustomNodeProps) => {
  const { 
    deleteNode, 
    handleAddChildNode,
    openRecommendationNodeId,
    openRecommendationPopover,
    closeRecommendationPopover,
    setSelectedNodeId,
    selectedNodeId
  } = useAnalysisPlanContext();
  
  // Check if this node's recommendation popover is open
  const isRecommendationOpen = openRecommendationNodeId === currentNode.id;

  const getNodeColor = (type?: string) => {
    switch (type) {
      case "metric":
        return "purple";
      default:
        return "purple";
    }
  };

  // Count of child nodes from all recommendations
  const getTotalChildCount = (): number => {
    if (!currentNode.recommendation_children_map) return 0;
    let count = 0;
    
    // Convert to Array.from to avoid TypeScript errors with Map iteration
    Array.from(currentNode.recommendation_children_map.entries()).forEach(([_, childIds]) => {
      count += childIds.length;
    });
    
    return count;
  };

  const bgColor = useColorModeValue("white", "gray.800");
  
  // Use selectedNodeId from context instead of selected prop for highlighting
  const isSelected = selectedNodeId === currentNode.id;
  const borderColor = isSelected ? getNodeColor(currentNode.type) : "gray.300";
  
  // Handle delete node
  const handleDelete = (event: React.MouseEvent) => {
    // Stop event from propagating to parent (node click)
    event.stopPropagation();
    deleteNode(currentNode.id);
  };
  
  // Handle add recommendation - prevent event propagation
  const handleAddRecommendation = (event: React.MouseEvent) => {
    closeRecommendationPopover();
    event.stopPropagation();
    openRecommendationPopover(currentNode?.id);
    setSelectedNodeId(currentNode?.id);
  };
  
  // Don't show delete button for root nodes
  const showDeleteButton = currentNode.type !== "metric";

  // Add a child node to a recommendation
  const addChildNode = async (recommendationIndex: number) => {
    if (!currentNode) return;
    await handleAddChildNode(recommendationIndex, currentNode);
  };

  return (
    <Box
      borderWidth="2px"
      borderRadius="md"
      borderColor={borderColor}
      bg={bgColor}
      boxShadow="md"
      width="216px"
      height="126px"
      position="relative"
      _hover={{ boxShadow: "md" }}
      zIndex={isSelected ? 10 : 1}
    >
      {/* Top Row with Controls */}
      <HStack
        position="absolute"
        top="2px"
        left="2px"
        right="2px"
        height="18px"
        alignItems="center"
        justifyContent="flex-end"
        zIndex="2"
        p={1}
        spacing={3}
      >

          {currentNode?.metrics && currentNode?.metrics?.length > 0 && (
            <Tooltip label={`Metrics: ${currentNode.selected_metrics?.map((metric) => metric.header?.name).join(", ")}`} placement="top">
               <IconButton
                aria-label="Metrics"
                icon={<Icon as={MdBarChart} boxSize={5} />}
                size="xxs"
                variant="ghost"
                colorScheme={getNodeColor(currentNode.type)}
                p={0}
              />
            </Tooltip>
          )}

          {/* Display filters */}
          {currentNode.selected_filters && currentNode.selected_filters.length > 0 && (
            <Tooltip
              label={`Filters: ${currentNode.selected_filters
                .map((filter) => getFilterDisplayString(filter))
                .join(", ")}`}
              placement="top"
            >
              <IconButton
                aria-label="Filters"
                icon={<Icon as={FaFilter} boxSize={4} />}
                size="xxs"
                variant="ghost"
                colorScheme={getNodeColor(currentNode.type)}
                p={0}
              />
            </Tooltip>
          )}
          {/* Display selected columns */}
          {currentNode.selected_columns && currentNode.selected_columns.length > 0 && (
            <Tooltip
              label={`Split by: ${currentNode.selected_columns
                .map((col) => col?.kg_column_header?.name || "Unnamed")
                .join(", ")}`}
              placement="top"
            >
              <IconButton
                aria-label="Columns"
                icon={<Icon as={AiFillPieChart} boxSize={4} />}
                size="xxs"
                variant="ghost"
                colorScheme={getNodeColor(currentNode.type)}
                p={0}
              />
            </Tooltip>
          )}
          


          {/* Display benchmarks */}
          {currentNode.selected_benchmarks && currentNode.selected_benchmarks.length > 0 && (
            <Tooltip
              label={`Benchmarks: ${currentNode.selected_benchmarks
                .map((benchmark) => benchmark.header?.name)
                .join(", ")}`}
              placement="top"
            >
              <IconButton
                aria-label="Benchmarks"
                icon={<Icon as={TbMathFunction} boxSize={5} />}
                size="xxs"
                variant="ghost"
                colorScheme={getNodeColor(currentNode.type)}
                p={0}
              />
            </Tooltip>
          )}
          
          {/* Delete button */}
          {showDeleteButton && (
            <Tooltip label="Delete node and its subtree" placement="top">
              <IconButton
                aria-label="Delete Node"
                icon={<Icon as={FaTrash} boxSize={3} />}
                size="xxs"
                variant="ghost"
                colorScheme="red"
                p={0}
                onClick={handleDelete}
              />
            </Tooltip>
          )}

      </HStack>

      {/* Node Label Center Layer */}
      <Flex
        position="absolute"
        top="24px"
        left="0"
        right="0"
        bottom="0"
        px={2}
        alignItems="center"
        justifyContent="center"
        zIndex="1"
      >
        <Tooltip 
          label={currentNode.label} 
          placement="top"
        >
          <Text
            fontSize="xs"
            fontWeight="600"
            color="gray.700"
            textAlign="center"
            noOfLines={4}
          >
            {currentNode.label}
          </Text>
        </Tooltip>
      </Flex>

      {/* Bottom Left - Recommendation indicator
      {currentNode?.recommendation_rules && currentNode?.recommendation_rules?.length > 0 && (
        <Tooltip
          label={`Recommendations: ${currentNode?.recommendation_rules?.length}, Child Nodes: ${getTotalChildCount()}`}
          placement="bottom"
        >
          <IconButton
            aria-label="Recommendations"
            icon={<FiColumns size="10px" />}
            size="xs"
            variant="ghost"
            colorScheme="gray"
            position="absolute"
            bottom="1px"
            left="1px"
            zIndex="2"
            minW="16px"
            height="16px"
            p={0}
          />
        </Tooltip>
      )} */}

      {/* Bottom Right - Add Recommendation Button */}
      <Popover
        isOpen={isRecommendationOpen}
        onClose={closeRecommendationPopover}
        placement="start-start"
        closeOnBlur={false}
        closeOnEsc={true}
        isLazy={true}
        strategy="fixed"
        colorScheme="purple"
      >
        <PopoverTrigger>
          <Tooltip label="Add next step" placement="bottom">
            <IconButton
              aria-label="Add Recommendation"
              icon={<Icon as={FaLongArrowAltRight} boxSize={4} />}
              size="xxs"
              variant="ghost"
              color="purple.300"
              position="absolute"
              bottom="1px"
              right="2px"
              zIndex="2"
              p={0}
              onClick={(e) => {
                e.stopPropagation();
                handleAddRecommendation(e);
              }}
              _hover={{ color: "purple.700" }}
            />
          </Tooltip>
        </PopoverTrigger>
        <Portal>
          <PopoverContent 
            width="400px" 
            maxWidth="400px" 
            maxHeight="400px"
            overflowY="auto"
            zIndex={9999}
            onClick={(e) => e.stopPropagation()}
            onMouseDown={(e) => e.stopPropagation()}
            boxShadow="2xl"
            border="1px solid"
            borderColor="gray.200"
            bg="white"
            position="absolute"
            mt={2}
            _focus={{ boxShadow: "none" }}
          >
          <PopoverHeader mb={0} px={2} py={0}>
            <Flex justifyContent="space-between" alignItems="center">
              <Box>
                <Text fontSize="md" fontWeight="600" color="gray.800" mb={0}>
                  Next Step
                </Text>
                {/* <Text fontSize="sm" color="gray.600" fontWeight="medium" mb={0}>
                  for "{currentNode.label}"
                </Text> */}
              </Box>
              <PopoverCloseButton position="relative" top={0} right={0} />
            </Flex>
          </PopoverHeader>
          <PopoverBody>
            <Recommendations 
              currentNode={currentNode} 
              addChildToRecommendation={addChildNode} 
            />
          </PopoverBody>
        </PopoverContent>
        </Portal>
      </Popover>

      {/* Handles */}
      <Handle
        type="target"
        position={Position.Left}
        style={{
          background: "#718096",
          width: "4px",
          height: "4px",
          zIndex: 3,
        }}
      />
      <Handle
        type="source"
        position={Position.Right}
        style={{
          background: "#718096",
          width: "4px",
          height: "4px",
          zIndex: 3,
        }}
      />
    </Box>
  );
};

export default memo(AnalysisStepNode); 